import { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/pro-regular-svg-icons/faBuilding';
import { useTranslation } from 'react-i18next';
import { autoSuggestOptionsApi } from 'AutoSuggestOptions';
import { MultiSelectDropdown, SelectedChips } from './FilterControls';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';
import 'StudentNetwork/locales/student_network/field_options-en.json';
import 'Careers/locales/careers/field_options-en.json';

// v1 industry keys from careers_module.js
const INDUSTRY_KEYS = [
    'consulting',
    'consumer_products',
    'education',
    'energy',
    'financial',
    'health_care',
    'legal',
    'manufacturing',
    'media',
    'military',
    'nonprofit',
    'retail',
    'real_estate',
    'technology',
    'other',
] as const;

// v1 interest keys from careers_module.js
const INTEREST_KEYS = [
    'travel',
    'finance',
    'entrepreneurship',
    'art',
    'education',
    'marketing',
    'health_care',
    'nonprofit',
    'sports',
    'technology',
    'data_science',
    'blockchain',
    'product_management',
    'design',
    'sales',
    'leadership',
    'mixology',
    'ai',
] as const;

// v1 looking-for keys from careers_module.js
const LOOKING_FOR_KEYS = [
    'co_founder',
    'study_partner',
    'collaborators_talent',
    'new_friends',
    'ideas_inspiration',
    'job_opportunities',
    'mentorship',
    'funding',
] as const;

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 200;

type SuggestOption = { id: string; text: string };
type SuggestResponse = { professionalOrganizationOptions?: SuggestOption[] };

// oxlint-disable-next-line max-lines-per-function -- autocomplete with dropdown and chips
function CompanyAutocomplete({
    companies,
    onCompaniesChange,
    placeholder,
}: {
    companies: string[];
    onCompaniesChange: (companies: string[]) => void;
    placeholder: string;
}) {
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [triggerFetch, { data }] = autoSuggestOptionsApi.useLazyAutoSuggestOptionsQuery();
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = useCallback(
        (text: string) => {
            setInputValue(text);
            if (debounceRef.current) clearTimeout(debounceRef.current);

            if (text.length >= MIN_SEARCH_LENGTH) {
                debounceRef.current = setTimeout(() => {
                    triggerFetch({
                        filters: {
                            type: 'professional_organization',
                            searchText: text,
                            inLocaleOrEn: 'en',
                            suggest: true,
                        },
                        limit: 10,
                    });
                    setIsOpen(true);
                }, DEBOUNCE_MS);
            } else {
                setIsOpen(false);
            }
        },
        [triggerFetch],
    );

    const suggestions: string[] = ((data as SuggestResponse | undefined)?.professionalOrganizationOptions ?? [])
        .map(o => o.text)
        .filter((t): t is string => !!t);

    const handleSelect = useCallback(
        (text: string) => {
            if (!companies.includes(text)) {
                onCompaniesChange([...companies, text]);
            }
            setInputValue('');
            setIsOpen(false);
        },
        [companies, onCompaniesChange],
    );

    const handleRemove = useCallback(
        (index: number) => {
            onCompaniesChange(companies.filter((_, i) => i !== index));
        },
        [companies, onCompaniesChange],
    );

    return (
        <div>
            <div ref={containerRef} className="relative">
                <div className="flex h-12 items-center gap-3 rounded-[8px] border border-transparent bg-slate-grey-50 px-4 transition-colors hover:border-slate-grey-300">
                    <FontAwesomeIcon icon={faBuilding} className="w-4 text-base text-slate-grey" />
                    <input
                        type="text"
                        value={inputValue}
                        onChange={e => handleInputChange(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && inputValue.trim()) {
                                e.preventDefault();
                                handleSelect(inputValue.trim());
                            }
                        }}
                        onFocus={() => {
                            if (suggestions.length > 0 && inputValue.length >= MIN_SEARCH_LENGTH) setIsOpen(true);
                        }}
                        placeholder={placeholder}
                        className="flex-1 bg-transparent text-base text-black outline-none placeholder:text-slate-grey"
                    />
                </div>
                {isOpen && suggestions.length > 0 && (
                    <div className="absolute inset-x-0 z-50 mt-1 max-h-48 overflow-y-auto rounded-[8px] bg-white py-1 shadow-[0_4px_12px_0_rgba(0,0,0,0.15)]">
                        {suggestions.map(text => (
                            <button
                                key={text}
                                type="button"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => handleSelect(text)}
                                className="w-full px-4 py-2 text-start text-base text-black hover:bg-slate-grey-50"
                            >
                                {text}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {companies.length > 0 && (
                <div className="mt-2">
                    <SelectedChips items={companies} onRemove={handleRemove} />
                </div>
            )}
        </div>
    );
}

type AdvancedFiltersProps = {
    companies: string[];
    onCompaniesChange: (companies: string[]) => void;
    industries: string[];
    onIndustriesChange: (industries: string[]) => void;
    interests: string[];
    onInterestsChange: (interests: string[]) => void;
    lookingFor: string[];
    onLookingForChange: (lookingFor: string[]) => void;
    timezones: string[];
    onTimezonesChange: (timezones: string[]) => void;
};

// oxlint-disable-next-line max-lines-per-function -- advanced filters with autocomplete
export function AdvancedFilters({
    companies,
    onCompaniesChange,
    industries,
    onIndustriesChange,
    interests,
    onInterestsChange,
    lookingFor,
    onLookingForChange,
    timezones,
    onTimezonesChange,
}: AdvancedFiltersProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.advancedFilters' });
    const { t: tCareersField } = useTranslation('back_royal', { keyPrefix: 'careers.field_options' });
    const { t: tNetworkField } = useTranslation('back_royal', { keyPrefix: 'student_network.field_options' });

    const industryOptions = INDUSTRY_KEYS.map(key => ({
        key,
        label: tCareersField(key, { defaultValue: key }),
    }));

    const interestOptions = INTEREST_KEYS.map(key => ({
        key,
        label: tNetworkField(key, { defaultValue: key }),
    }));

    const lookingForOptions = LOOKING_FOR_KEYS.map(key => ({
        key,
        label: tNetworkField(key, { defaultValue: key }),
    }));

    const timezoneOptions = Intl.supportedValuesOf('timeZone').map(tz => ({ key: tz, label: tz }));

    return (
        <div className="flex flex-col gap-5">
            {/* Company search with autocomplete */}
            <CompanyAutocomplete
                companies={companies}
                onCompaniesChange={onCompaniesChange}
                placeholder={t('companySearch')}
            />

            {/* Industry */}
            <MultiSelectDropdown
                label={t('industry')}
                placeholder={t('selectIndustry')}
                options={industryOptions}
                selected={industries}
                onChange={onIndustriesChange}
            />

            {/* Interests */}
            <MultiSelectDropdown
                label={t('interests')}
                placeholder={t('selectInterest')}
                options={interestOptions}
                selected={interests}
                onChange={onInterestsChange}
            />

            {/* Looking For */}
            <MultiSelectDropdown
                label={t('lookingFor')}
                placeholder={t('selectFromList')}
                options={lookingForOptions}
                selected={lookingFor}
                onChange={onLookingForChange}
            />

            {/* Timezone */}
            <MultiSelectDropdown
                label={t('timezone')}
                placeholder={t('selectTimezones')}
                options={timezoneOptions}
                selected={timezones}
                onChange={onTimezonesChange}
                searchable
            />
        </div>
    );
}
